import {Component} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FloatLabel} from 'primeng/floatlabel';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {Button} from 'primeng/button';
import {SelectButton} from 'primeng/selectbutton';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {CommonModule} from '@angular/common';
import {debounceTime, distinctUntilChanged, Subject, switchMap, of} from 'rxjs';
import {DialogAction} from '../../../../shared/contracts/dialog.interface';
import {ConversationPriority, ConversationStatus} from '../../../contracts/conversation.interface';
import {PHONE_PREFIXES} from '../../../../contacts/features/dialogs/contact-dialog/contact-dialog.component';
import {ContactService, ContactSearchItem} from '../../../../contacts/services/contact.service';
import {ContactPhoneNumber} from '../../../../contacts/contracts/contact.interface';

type InputMode = 'number' | 'contact';

@Component({
  selector: 'kc-create-conversation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FloatLabel,
    InputText,
    Select,
    Button,
    SelectButton,
    AutoComplete,
  ],
  templateUrl: './create-conversation-dialog.component.html',
})
export class CreateConversationDialogComponent {
  static readonly DIALOG_CONFIG: DynamicDialogConfig = {
    header: 'Nuova Conversazione',
    width: '95vw',
    style: {'max-width': '480px'},
    modal: true,
    focusOnShow: false,
  };

  form: FormGroup;
  phonePrefixes = PHONE_PREFIXES;

  priorityOptions = [
    {label: 'Normale', value: ConversationPriority.DEFAULT},
    {label: 'Bassa', value: ConversationPriority.LOW},
    {label: 'Media', value: ConversationPriority.MEDIUM},
    {label: 'Alta', value: ConversationPriority.HIGH},
  ];

  statusOptions = [
    {label: 'Aperta', value: ConversationStatus.OPEN},
    {label: 'Da Assegnare', value: ConversationStatus.TO_ASSIGN},
  ];

  modeOptions = [
    {label: 'Numero', value: 'number' as InputMode},
    {label: 'Contatto', value: 'contact' as InputMode},
  ];

  inputMode: InputMode = 'number';

  contactSuggestions: ContactSearchItem[] = [];
  selectedContact: ContactSearchItem | null = null;
  selectedPhone: ContactPhoneNumber | null = null;

  private searchInput$ = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private contactService: ContactService,
  ) {
    this.form = this.fb.group({
      prefix: ['39', Validators.required],
      localNumber: ['', [Validators.required, Validators.pattern(/^\d{6,15}$/)]],
      priority: [ConversationPriority.DEFAULT, Validators.required],
      status: [ConversationStatus.OPEN, Validators.required],
    });

    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => q.trim().length >= 2 ? this.contactService.search(q) : of({data: []})),
    ).subscribe(res => {
      this.contactSuggestions = res.data;
    });
  }

  onModeChange() {
    this.selectedContact = null;
    this.selectedPhone = null;
    this.contactSuggestions = [];
    this.form.patchValue({prefix: '39', localNumber: ''});
  }

  onContactSearch(event: AutoCompleteCompleteEvent) {
    this.searchInput$.next(event.query);
  }

  onContactSelect(contact: ContactSearchItem) {
    this.selectedContact = contact;
    this.selectedPhone = null;

    if (contact.phoneNumbers.length === 1) {
      this.applyPhone(contact.phoneNumbers[0]);
    }
  }

  selectPhone(phone: ContactPhoneNumber) {
    this.selectedPhone = phone;
    this.applyPhone(phone);
  }

  private applyPhone(phone: ContactPhoneNumber) {
    const raw = phone.phoneNumber.replace(/\D/g, '');
    const matchedPrefix = PHONE_PREFIXES.find(p => raw.startsWith(p.value));
    if (matchedPrefix) {
      this.form.patchValue({
        prefix: matchedPrefix.value,
        localNumber: raw.slice(matchedPrefix.value.length),
      });
    } else {
      this.form.patchValue({prefix: '39', localNumber: raw});
    }
  }

  contactLabel(contact: ContactSearchItem): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  }

  get isContactModeValid(): boolean {
    if (this.inputMode !== 'contact') return true;
    return this.selectedContact !== null && this.form.get('localNumber')?.value !== '';
  }

  save() {
    if (this.form.invalid) return;
    if (!this.isContactModeValid) return;
    const {prefix, localNumber, priority, status} = this.form.value;
    this.ref.close({
      action: DialogAction.OK,
      data: {phone_number: prefix + localNumber, priority, status},
    });
  }

  closeDialog() {
    this.ref.close({action: DialogAction.CLOSE});
  }
}
